<template>
    <p-search-screen
        title="Invoices"
        api-destination="invoices"
        edit-key="invoiceId"
        :filters="filters"
        :fields="fields"
        :filterBy="filterBy"
        :use-bootstrap-selection="false"
        add-new-button
        ref="search"
        @selected="onSelection"
        @selected-rows-changed="selectedRowsChanged"        
        submit-search-as-post
    >

        <template v-slot:actionButtons>

          <router-link to="/invoices/newexp" class="btn btn-secondary"
          >
            <b-icon-plus></b-icon-plus>Misc Expenses</router-link>
            <b-dropdown
                text="Export"
                :is-busy="isBusy"> 
                <b-dropdown-item
                    @click="exportInvoiceSummary('csv')"
                    :disabled="selectedInvoices.length <= 0"
                    >Invoice Summary (CSV)</b-dropdown-item>
                <b-dropdown-item
                    @click="exportInvoiceSummary('html')"
                    :disabled="selectedInvoices.length <= 0"
                    >Invoice Summary (View/Print)</b-dropdown-item>
                <b-dropdown-item @click="exportInvoiceDetail('csv')" :disabled="selectedInvoices.length <= 0">
                    Invoice Detail (CSV)</b-dropdown-item>
                <b-dropdown-item @click="exportInvoiceDetail('html')" :disabled="selectedInvoices.length <= 0">
                    Invoice Detail (View/Print)</b-dropdown-item>
                <b-dropdown-item @click="exportAgingByInvoice('csv')" :disabled="selectedInvoices.length <= 0">
                    Invoice Aging by Invoice (CSV)</b-dropdown-item>
                <b-dropdown-item @click="exportAgingByInvoice('html')" :disabled="selectedInvoices.length <= 0">
                    Invoice Aging by Invoice (View/Print)</b-dropdown-item>                
                <b-dropdown-item @click="exportAgingBySupplier('csv')">
                    Invoice Aging by Supplier (CSV)</b-dropdown-item>
                <b-dropdown-item @click="exportAgingBySupplier('html')">
                    Invoice Aging by Supplier (View/Print)</b-dropdown-item>
            </b-dropdown>
        </template>
        <template v-slot:belowSearch>
            <p-form>
                <b-row class="ms-3 mt-4 me-2">
                    <p-checkbox label="Open" class="ms-2" v-model="filters.open" />
                    <p-checkbox label="Posted" class="ms-3" v-model="filters.posted" />
                    <p-checkbox label="Cancelled" class="ms-3" v-model="filters.cancelled" />
                    <p-checkbox label="Paid" class="ms-3" v-model="filters.paid" />
                    <b-col md="3" class="ml-md-auto">
                        <p-datepicker label="Invoice Date: From" vid="invoiceBeginDate" v-model="filters.beginDate" />
                    </b-col>
                    <b-col md="3">
                        <p-datepicker label="Invoice Date: To" v-model="filters.endDate"
                            rules="dateGreaterOrEqual:@invoiceBeginDate" is-end-date />
                    </b-col>
                </b-row>
            </p-form>
        </template>
    </p-search-screen>
</template>

<script>
import { currencyFormatter, dateFormatter } from '../../components/Common/Formatters.js';
import SearchStorage from '@/components/mixins/SearchFilterStorage.js';
import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';

export default {
    mixins: [SearchStorage],
    data() {
        return {
            isBusy: false,
            selectedInvoices: [],
            filterName: 'InvoicesFilter',
            filters: {
                automatic: "",
                perPage: 100,
                sortBy: 'invoiceDate',
                sortDesc: true,
                beginDate: null,
                endDate: null,
                open: true,
                posted: false,
                cancelled: false,
                paid: false,
                searchBy: 'All',
                selectedIds: null
            },
            filterExclude: [
                'selectedIds' // Do not save selectedIds in local storage
            ],            
            filterBy: ['All', 'Invoice #', 'ISBN', 'Barcode', 'Catalog #', 'Supplier', 'PO #', 'SKU'],
            fields: [
                {
                    key: 'type',
                    sortable: true
                },
                {
                    key: 'invoiceNumber',
                    sortable: true
                },
                {
                    key: 'supplier',
                    sortable: true
                },
                {
                    key: 'invoiceDate',
                    formatter: dateFormatter,
                    sortable: true
                },
                {
                    key: 'total',
                    thClass: 'text-center',
                    tdClass: 'text-end',
                    formatter: currencyFormatter,
                    sortable: true
                },
                {
                    key: 'attachmentCount',
                    label: 'Attachments',
                    sortable: true
                },
                {
                    key: 'status',
                    sortable: true
                }
            ]
        };
    },
    methods: {
        onSelection(invoices) {
            this.selectedInvoices = invoices;
        },
        selectedRowsChanged(selectedInvoices) {
            this.onSelection(selectedInvoices);
        },
        async exportInvoiceDetail(format) {
            this.isBusy = true;

            this.filters.selectedIds = this.selectedInvoices?.map(invoice => invoice.invoiceId);
            if (format == 'csv') {
                let apiUrl = '/invoices/exportinvoicedetail';
                this.filters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
                axios.post(
                    apiUrl,
                    {
                        searchFilter: this.filters,
                        exportFilter: {
                            locationId: this.filters.locationId,
                            exportFormat: format
                        }
                    },
                    { 
                        responseType: 'blob' 
                    })
                    .then(result => {
                        downloadFileToBrowser(
                            result.data,
                            'InvoiceDetail.csv',
                            'application/csv');

                        this.$appSuccess('Invoice Detail CSV file generated successfully.')
                    })
                    .finally(() => {
                        this.finalizeExport();                    
                    });
            }
            if (format == 'html') {
                let apiUrl='/invoices/exportinvoicedetail';
                this.filters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
                this.isBusy = true;
                axios.post(
                    apiUrl,
                    {
                        searchFilter: this.filters,
                        exportFilter: {
                            locationId: this.filters.locationId,
                            exportFormat: format
                        }
                    },
                    { 
                        responseType: 'text/html'
                    }
                )
                .then(result => {
                    var htmlContent = result.data;
                    var newTab = window.open();
                    newTab.document.write(htmlContent);
                    newTab.document.title = 'Invoice Detail';                    
                    newTab.document.close();

                    this.$appSuccess('Invoice Detail report generated successfully.')
                })
                .finally(() => {
                    this.finalizeExport();
                });
            }            
        },
        async exportAgingBySupplier(format) {
            this.isBusy = true;

            if (format == 'csv') {
                let apiUrl = '/invoices/exportinvoiceagingbysupplier';
                this.filters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
                axios.post(
                    apiUrl,
                    {
                        locationId: this.filters.locationId,
                        exportFormat: format
                    },
                    { 
                        responseType: 'blob' 
                    })
                    .then(result => {
                        downloadFileToBrowser(
                            result.data,
                            'InvoiceAgingBySupplier.csv',
                            'application/csv');

                        this.$appSuccess('Invoice Aging by Supplier CSV file generated successfully.')
                    })
                    .finally(() => {
                        this.finalizeExport();
                    });
            }
            if (format == 'html') {
                let apiUrl='/invoices/exportinvoiceagingbysupplier';
                this.filters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
                this.isBusy = true;
                axios.post(
                    apiUrl,
                    {
                        locationId: this.filters.locationId,
                        exportFormat: format
                    },
                    { 
                        responseType: 'text/html'
                    }
                )
                .then(result => {
                    var htmlContent = result.data;
                    var newTab = window.open();
                    newTab.document.write(htmlContent);
                    newTab.document.close();

                    this.$appSuccess('Invoice Aging by Supplier report generated successfully.')
                })
                .finally(() => {
                    this.finalizeExport();
                });
            }            
        },
        exportInvoiceSummary(format) {
            this.isBusy = true;
            this.filters.selectedIds = this.selectedInvoices?.map(invoice => invoice.invoiceId);
            switch(format) {
                case 'csv':
                    axios
                        .post('invoices/exportinvoicesummarycsv', this.filters, { responseType: 'blob' })
                        .then((result) => {
                            downloadFileToBrowser(result.data, 'Invoice Summary.csv', 'application/csv');
                            this.$appSuccess(`Invoice Summary generated successfully.`);
                        })
                        .finally(() => {
                            this.finalizeExport();
                        });
                    break;
                case 'html':
                    axios
                        .post('invoices/exportinvoicesummaryhtml', this.filters, {responseType: 'text/html' })
                        .then((result) => {
                            var htmlContent = result.data;
                                var newTab = window.open();
                                newTab.document.write(htmlContent);
								newTab.document.title = 'Invoice Summary';
                                newTab.document.close();
                                this.$appSuccess(`Invoice Summary generated successfully.`);
                        })
                        .finally(() => {
                            this.finalizeExport();
                        });
                    break;
            }
        },
        finalizeExport() {
            this.isBusy = false;
            this.filters.selectedIds = null;
        },
        async exportAgingByInvoice(format) {
            this.isBusy = true;

            this.filters.selectedIds = this.selectedInvoices?.map(invoice => invoice.invoiceId);
            if (format == 'csv') {
                let apiUrl = '/invoices/exportinvoiceagingbyinvoice';
                this.filters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
                axios.post(
                    apiUrl,
                    {
                        searchFilter: this.filters,
                        exportFilter: {
                        locationId: this.filters.locationId,
                        exportFormat: format
                        }
                    },
                    { 
                        responseType: 'blob' 
                    })
                    .then(result => {
                        downloadFileToBrowser(
                            result.data,
                            'InvoiceAgingByInvoice.csv',
                            'application/csv');

                        this.$appSuccess('Invoice Aging by Invoice CSV file generated successfully.')
                    })
                    .finally(() => {
                        this.finalizeExport();
                    });
            }
            if (format == 'html') {
                let apiUrl='/invoices/exportinvoiceagingbyinvoice';
                this.filters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
                this.isBusy = true;
                axios.post(
                    apiUrl,
                    {
                        searchFilter: this.filters,
                        exportFilter: {
                        locationId: this.filters.locationId,
                        exportFormat: format
                        }
                    },
                    { 
                        responseType: 'text/html'
                    }
                )
                .then(result => {
                    var htmlContent = result.data;
                    var newTab = window.open();
                    newTab.document.write(htmlContent);
                    newTab.document.close();

                    this.$appSuccess('Invoice Aging by Invoice report generated successfully.')
                })
                .finally(() => {
                    this.finalizeExport();
                });
            }            
        }        
    }
};
</script>
<style scoped></style>
